import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Video, VideoOff, Users, MessageSquare, PhoneOff, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { videoAPI } from '../services/api';

const JitsiMeet = ({ 
  roomName, 
  domain = 'meet.canada-englishlife.com',
  config,
  userInfo,
  onClose,
  onRecordingUploaded,
  isTeacher = false,
  sessionTitle = 'Live Class',
  sessionId = null
}) => {
  const jitsiContainerRef = useRef(null);
  const apiRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const [error, setError] = useState(null);
  const [participantCount, setParticipantCount] = useState(1);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start screen/tab recording
  const startAutoRecording = useCallback(async () => {
    if (!isTeacher || !sessionId) return;
    
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        systemAudio: 'include'
      });

      let audioStream = null;
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        console.log('Could not capture microphone:', e);
      }

      const tracks = [...displayStream.getTracks()];
      if (audioStream) {
        audioStream.getAudioTracks().forEach(track => tracks.push(track));
      }
      
      const combinedStream = new MediaStream(tracks);
      streamRef.current = combinedStream;

      const options = { mimeType: 'video/webm;codecs=vp9,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setRecordingStatus('processing');
        const blob = new Blob(recordedChunksRef.current, { type: options.mimeType });
        
        if (blob.size > 0 && sessionId) {
          await uploadRecording(blob);
        } else {
          setRecordingStatus('idle');
        }
      };

      mediaRecorder.onerror = (error) => {
        console.error('MediaRecorder error:', error);
        setRecordingStatus('error');
        setIsRecording(false);
      };

      displayStream.getVideoTracks()[0].onended = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopAutoRecording();
        }
      };

      mediaRecorder.start(10000);
      setIsRecording(true);
      setRecordingStatus('recording');
      recordingStartTimeRef.current = Date.now();
      
      durationIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          setRecordingDuration(Math.floor((Date.now() - recordingStartTimeRef.current) / 1000));
        }
      }, 1000);

      console.log('Auto-recording started');
    } catch (err) {
      console.error('Failed to start auto-recording:', err);
      setRecordingStatus('idle');
    }
  }, [isTeacher, sessionId]);

  const stopAutoRecording = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  }, []);

  const uploadRecording = async (blob) => {
    if (!sessionId || !blob || blob.size === 0) {
      setRecordingStatus('idle');
      return;
    }

    setRecordingStatus('uploading');
    setUploadProgress(0);

    try {
      const fileName = `recording_${sessionId}_${Date.now()}.webm`;
      const file = new File([blob], fileName, { type: blob.type });

      const response = await videoAPI.uploadRecording(sessionId, file);
      
      setUploadProgress(100);
      setRecordingStatus('uploaded');
      
      if (onRecordingUploaded) {
        onRecordingUploaded(response.data);
      }

      console.log('Recording uploaded successfully:', response.data);
    } catch (err) {
      console.error('Failed to upload recording:', err);
      setRecordingStatus('error');
      
      // Save locally as fallback
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `class_recording_${sessionId}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Recording saved locally. You can upload it manually later.');
    }
  };

  useEffect(() => {
    const loadJitsiScript = () => {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) {
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[src*="external_api.js"]');
        if (existingScript) {
          if (window.JitsiMeetExternalAPI) {
            resolve();
          } else {
            existingScript.addEventListener('load', resolve);
            existingScript.addEventListener('error', reject);
          }
          return;
        }

        const script = document.createElement('script');
        script.src = `https://${domain}/external_api.js`;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const initJitsi = async () => {
      try {
        await loadJitsiScript();
        
        if (!jitsiContainerRef.current) return;

        const jitsiConfig = {
          roomName: roomName,
          parentNode: jitsiContainerRef.current,
          width: '100%',
          height: '100%',
          userInfo: {
            displayName: userInfo?.name || 'Participant',
            email: userInfo?.email || ''
          },
          configOverwrite: {
            disablePrivateMessages: true,
            enableChat: true,
            whiteboard: {
              enabled: true,
              collabServerBaseUrl: 'https://excalidraw-backend.jitsi.net'
            },
            fileRecordingsEnabled: false,
            localRecording: { enabled: false },
            enableLobby: false,
            startWithAudioMuted: !isTeacher,
            startWithVideoMuted: false,
            enableNoisyMicDetection: true,
            prejoinPageEnabled: false,
            subject: sessionTitle,
            disableInviteFunctions: true,
            hideConferenceSubject: false,
            hideConferenceTimer: false,
            disableReactions: false,
            disablePolls: false,
            enableInsecureRoomNameWarning: false,
            ...config?.configOverwrite
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'chat',
              'settings', 'raisehand', 'videoquality', 'filmstrip',
              'participants-pane', 'tileview', 'whiteboard',
              'noisesuppression', 'screenshare', 'highlight'
            ],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            MOBILE_APP_PROMO: false,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
            FILM_STRIP_MAX_HEIGHT: 120,
            VIDEO_LAYOUT_FIT: 'both',
            VERTICAL_FILMSTRIP: true,
            DEFAULT_BACKGROUND: '#1a1a2e',
            SETTINGS_SECTIONS: ['devices', 'language', 'profile'],
            ...config?.interfaceConfigOverwrite
          }
        };

        apiRef.current = new window.JitsiMeetExternalAPI(domain, jitsiConfig);

        // Hide loading after short delay (Jitsi iframe is rendering)
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);

        apiRef.current.addListener('videoConferenceJoined', () => {
          setIsLoading(false);
          setHasJoined(true);
          console.log('Joined video conference');
        });

        apiRef.current.addListener('videoConferenceLeft', () => {
          console.log('Left video conference');
          if (isRecording) {
            stopAutoRecording();
          }
        });

        apiRef.current.addListener('participantJoined', () => {
          if (apiRef.current) {
            apiRef.current.getParticipantsInfo().then(participants => {
              setParticipantCount(participants.length);
            });
          }
        });

        apiRef.current.addListener('participantLeft', () => {
          if (apiRef.current) {
            apiRef.current.getParticipantsInfo().then(participants => {
              setParticipantCount(participants.length);
            });
          }
        });

        apiRef.current.addListener('readyToClose', () => {
          if (isRecording) {
            stopAutoRecording();
          }
          if (onClose) onClose();
        });

        apiRef.current.addListener('errorOccurred', (error) => {
          console.error('Jitsi error:', error);
          setError(error.message || 'Connection error occurred');
          setIsLoading(false);
        });

      } catch (err) {
        console.error('Failed to initialize Jitsi:', err);
        setError('Failed to load video conferencing. Please refresh and try again.');
        setIsLoading(false);
      }
    };

    initJitsi();

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, [roomName, domain, config, userInfo, isTeacher, sessionTitle, onClose, stopAutoRecording, isRecording]);

  const handleEndCall = async () => {
    if (isRecording) {
      stopAutoRecording();
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (apiRef.current) {
      apiRef.current.executeCommand('hangup');
    }
    if (onClose) onClose();
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopAutoRecording();
    } else {
      startAutoRecording();
    }
  };

  const RecordingIndicator = () => {
    switch (recordingStatus) {
      case 'recording':
        return (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/30 px-3 py-1 rounded-full">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span>REC {formatDuration(recordingDuration)}</span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center gap-2 text-yellow-400 text-sm bg-yellow-900/30 px-3 py-1 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Processing...</span>
          </div>
        );
      case 'uploading':
        return (
          <div className="flex items-center gap-2 text-blue-400 text-sm bg-blue-900/30 px-3 py-1 rounded-full">
            <Upload className="w-3 h-3 animate-bounce" />
            <span>Uploading...</span>
          </div>
        );
      case 'uploaded':
        return (
          <div className="flex items-center gap-2 text-green-400 text-sm bg-green-900/30 px-3 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            <span>Saved!</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-2 text-orange-400 text-sm bg-orange-900/30 px-3 py-1 rounded-full">
            <AlertCircle className="w-3 h-3" />
            <span>Local</span>
          </div>
        );
      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <VideoOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={onClose} variant="outline" className="w-full">
            Close & Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-white">
            <Video className="w-5 h-5 text-teal-400" />
            <span className="font-medium truncate max-w-[250px]">{sessionTitle}</span>
          </div>
          {hasJoined && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Users className="w-4 h-4" />
              <span>{participantCount}</span>
            </div>
          )}
          <RecordingIndicator />
        </div>
        <div className="flex items-center gap-2">
          {isTeacher && hasJoined && (
            <Button
              onClick={handleToggleRecording}
              variant={isRecording ? "destructive" : "outline"}
              size="sm"
              className={isRecording ? "" : "text-white border-gray-600 hover:bg-gray-700"}
              disabled={recordingStatus === 'processing' || recordingStatus === 'uploading'}
            >
              {isRecording ? (
                <>
                  <div className="w-2 h-2 bg-white rounded-full mr-2" />
                  Stop Recording
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-2" />
                  Start Recording
                </>
              )}
            </Button>
          )}
          <Button
            onClick={handleEndCall}
            variant="destructive"
            size="sm"
            className="flex items-center gap-2"
            disabled={recordingStatus === 'processing' || recordingStatus === 'uploading'}
          >
            <PhoneOff className="w-4 h-4" />
            {recordingStatus === 'uploading' ? 'Uploading...' : 'End Call'}
          </Button>
        </div>
      </div>

      {/* Recording info banner */}
      {isTeacher && hasJoined && recordingStatus === 'idle' && !isRecording && (
        <div className="bg-teal-900/50 px-4 py-2 text-center text-teal-200 text-sm">
          📹 Click "Start Recording" to capture this class. Recording will auto-upload when you end the call.
        </div>
      )}

      {/* Jitsi Container */}
      <div className="flex-1 relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Loading video class...</p>
              <p className="text-gray-400 text-sm mt-2">Please allow camera and microphone access</p>
            </div>
          </div>
        )}

        <div 
          ref={jitsiContainerRef} 
          className="w-full h-full"
          style={{ minHeight: 'calc(100vh - 56px)' }}
        />
      </div>

      {/* Info bar for students */}
      {!isTeacher && hasJoined && (
        <div className="bg-gray-800 px-4 py-2 text-center text-gray-400 text-sm">
          <MessageSquare className="w-4 h-4 inline mr-2" />
          Group chat only. Private messaging is disabled.
        </div>
      )}
    </div>
  );
};

export default JitsiMeet;
